/** Modal de share (App-Livrari): link public 24h/7z/30z, copiere, dezactivare, „vezi pagina publică". */
import { useState } from 'react';
import { Modal, Spinner } from './ui';
import { useMutation, useShareLinks, useInvalidate, qk } from '../api/hooks';
import { createShareLink, revokeShareLink } from '../api/endpoints';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../lib/format';

const OPTIONS = [
  { days: 1, label: '24 de ore' },
  { days: 7, label: '7 zile' },
  { days: 30, label: '30 de zile' },
];

export function ShareModal({ clipId, open, onClose }: { clipId: string; open: boolean; onClose: () => void }) {
  const [days, setDays] = useState(7);
  const links = useShareLinks(open ? clipId : undefined);
  const invalidate = useInvalidate();
  const toast = useToast();

  const createMut = useMutation({
    mutationFn: () => createShareLink(clipId, days),
    onSuccess: () => {
      invalidate([qk.shares(clipId)]);
      toast.push({ kind: 'ok', title: 'Link public creat' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare la creare link', body: humanError(err) }),
  });

  const revokeMut = useMutation({
    mutationFn: (linkId: string) => revokeShareLink(clipId, linkId),
    onSuccess: () => {
      invalidate([qk.shares(clipId)]);
      toast.push({ kind: 'info', title: 'Link dezactivat' });
    },
  });

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.push({ kind: 'ok', title: 'Link copiat în clipboard' });
    } catch {
      toast.push({ kind: 'warn', title: 'Copiază manual', body: url });
    }
  }

  const items = links.data?.items ?? [];

  return (
    <Modal open={open} onClose={onClose} title="Partajează clipul" width={520}>
      <p style={{ margin: '0 0 14px', fontSize: 13.5, color: 'var(--muted)' }}>
        Creezi un link public de vizionare, cu expirare. Oricine îl are poate vedea clipul — fără cont.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {OPTIONS.map((o) => (
          <button key={o.days} className="chip" data-on={days === o.days} onClick={() => setDays(o.days)}>
            {o.label}
          </button>
        ))}
        <button className="btn btn-primary btn-sm" data-testid="create-share" onClick={() => createMut.mutate()} disabled={createMut.isPending} style={{ marginLeft: 'auto' }}>
          {createMut.isPending ? <Spinner /> : '+ Creează link'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.length === 0 && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Niciun link activ încă.</p>}
        {items.map((l) => (
          <div key={l.id} className="card" data-testid="share-link-row" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="pill" style={{ background: l.active ? 'var(--ok-soft)' : 'var(--bg2)', color: l.active ? 'var(--ok)' : 'var(--muted)' }}>
                {l.active ? 'Activ' : 'Dezactivat'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>valabil până pe {formatDate(l.expiresAt)}</span>
              <span className="tnum" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)' }}>{l.viewCount} vizionări</span>
            </div>
            <div className="mono" style={{ fontSize: 11.5, wordBreak: 'break-all', color: 'var(--ink)', background: 'var(--bg2)', borderRadius: 8, padding: '8px 10px' }} data-testid="share-url">
              {l.url}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => copy(l.url)}>Copiază</button>
              <a className="btn btn-ghost btn-sm" href={new URL(l.url).pathname} target="_blank" rel="noreferrer">Vezi pagina publică ↗</a>
              {l.active && (
                <button className="btn btn-ghost btn-sm" data-testid="revoke-share" onClick={() => revokeMut.mutate(l.id)} style={{ color: 'var(--err)', marginLeft: 'auto' }}>
                  Dezactivează
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
